import React, { useRef, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import './style.scss';
import "freespace-react-components/style.css";

// Import custom hooks
import {
  useSignageData,
  useMarkers,
  useDimensions,
  useGridConfig,
  usePerformanceMonitoring
} from './hooks';

// Import components
import {
  LoadingState,
  ErrorState,
  NoDataState,
  MainArea,
  SideArea
} from './components';

// Import utilities
import { useComponentRenderer } from './utils';

const Signage = ({ signageState, windowProps }) => {
  // Container reference for dimensions
  const containerRef = useRef(null);
  
  // Performance monitoring
  const { renderCountRef, trackMainComponentRender } = usePerformanceMonitoring();
  
  // Track main component renders
  trackMainComponentRender();
  
  // State management using FreespaceClient pattern
  const client = signageState?.client;
  const planData = useMemo(() => signageState?.locations?.[0]?.plan, [signageState?.locations]);
  const shortName = useMemo(() => signageState?.locations?.[0]?.shortName, [signageState?.locations]);
  
  console.log('shortName', shortName);

  // Extract data from signageState (following FreespaceClient pattern)
  const currentSignage = signageState?.signage;
  const imageUrl = currentSignage?.imageUrl;
  const screenId = currentSignage?.id || 6999; // Fallback to existing screen ID
  const orientation = currentSignage?.orientation || "landscape";
  
  // Custom hooks for data management
  const { signageDesign, transformedComponents, loading, error } = useSignageData(client, screenId, signageState);
  const { markers } = useMarkers(signageState);
  const containerDimensions = useDimensions(containerRef);
  const gridConfig = useGridConfig(containerDimensions, transformedComponents.allComponents);
  
  // Component rendering utilities
  const { renderGridComponent } = useComponentRenderer(gridConfig);
 
  
  // Legacy signage state processing (preserved for compatibility)
  const legacySignage = useMemo(() => signageState?.signage, [signageState?.signage]);
  
  const currentLocation = signageState?.locations?.find(l => l.id === legacySignage?.locationId);
  let currentDisplayElements = signageState?.floorElements?.[signageState?.currentSignageId];
  let featureDisplayElements = signageState?.floorElements?.[signageState?.currentSignageId];
  featureDisplayElements = featureDisplayElements?.filter(e => e.type === 'FEATURE') || [];
  const currentColony = signageState?.colonies?.[0];
  
  var remainingSpace = null;
  if (currentColony?.availability) {
    for (let i = 0; i < currentColony.availability.length; i++) {
      remainingSpace += currentColony.availability[i]?.current;
    }
  }
 
  const legacyOrientation = legacySignage?.orientation;
  const { plan } = currentLocation || {};
 
  const spaces = signageState?.spaces?.filter(space => 
    space.locationId === currentLocation?.id && 
    Object.prototype.hasOwnProperty.call(space.senses || {}, 'availability')
  ) || [];
  
   // Assuming your array is stored in a variable called `data`
   const filtered = spaces.filter(item => item.category?.showOnSignage === true);

   console.log('filtered spaces', filtered);
   // Group by category name
   const grouped = filtered.reduce((acc, item) => {
     const categoryName = item.category?.name || 'Unknown';

     if (!acc[categoryName]) {
       acc[categoryName] = [];
     }

     acc[categoryName].push(item);
     return acc;
   }, {});

   // Transform grouped data to the desired format
   const categories = Object.entries(grouped).map(([categoryName, items]) => {
     // Type assertion for items array
     const itemsArray = items;
     
     // Count available spaces (state 1 = available, state 0 = occupied)
     const availableCount = itemsArray.filter(item => 
       item.senses?.availability?.state === 1
     ).length;

     // Generate category ID from name (lowercase, replace spaces with hyphens)
     const categoryId = categoryName.toLowerCase().replace(/\s+/g, '-');

     // Get color from the first item's category color
     const categoryColor = itemsArray[0]?.category?.color || '#CCCCCC';

     return {
       id: categoryId,
       name: categoryName,
       color: categoryColor,
       availability: [itemsArray.length]
     };
   });

   console.log('categories data', categories);
  
  if (plan && legacyOrientation) {
    spaces.forEach(s => s.setPositionForView?.(plan.width, plan.height, legacyOrientation));
    currentDisplayElements?.forEach(s => s.setPositionForView?.(plan.width, plan.height, legacyOrientation));
  }
  
  const swapWH = Math.abs(legacyOrientation || 0) % 180 !== 0;
  let SignageOnMs = window.location.hostname === "local.afreespace.com" ? true : false;
  const image = {
    url: SignageOnMs ? legacySignage?.localImageUrl : legacySignage?.imageUrl,
    width: swapWH ? (plan?.height || 1920) : (plan?.width || 1080),
    height: swapWH ? (plan?.width || 1080) : (plan?.height || 1920),
  };
 
  /* Start ->  Filter Firemarshal Data */
  var firemarshalUserData = signageState?.firemarshalData?.$mobx?.values?.filter(data => data?.user?.group?.[0]?.type == "FIRE_MARSHAL" || data?.user?.group?.[0]?.type == "DOCTOR" || data?.user?.group?.[0]?.type == "FIRST_AIDERS")
  var firemarshalFinalData = [];
 
  for (let i = 0; i < firemarshalUserData?.length; i++) {
    var firemarshalDataList = spaces.filter(data => data.id == firemarshalUserData?.[i]?.space?.id);
    var spaceData = firemarshalDataList[0];
    if (spaceData != undefined) {
      spaceData.user = firemarshalUserData?.[i];
      firemarshalFinalData.push(spaceData);
    }
  }
  /* End ->  Filter Firemarshal Data */
 
  const magicUnusable = 8;
 
  let dimensions = {
    width: 0,
    height: 0,
  };
 
  const direction = 'horizontal';
  let span = currentSignage.imageSpanPercent / 110.0;
  // Updated color mapping using state-based colors
  const color = { 
    reserved: '#007bff',    // Blue for allocated/reserved
    occupied: '#dc3545',    // Red for occupied/presence  
    available: '#28a745',   // Green for available/absence
    'checked In': '#fd7e14', // Orange for checked in
    'checked Out': '#28a745' // Green for checked out
  };
 
  switch (direction) {
    case 'vertical':
      dimensions = {
        width: `calc(${span} * (100vw - ${magicUnusable}vmin))`,
        height: `calc((${span} *  ${image.height}/${image.width}) * (100vw - ${magicUnusable}vmin))`,
      };
      break;
    case 'horizontal':
    default:
      dimensions = {
        height: `calc(${span} * (100vh - ${magicUnusable}vmin))`,
        width: `calc((${span} *  ${image.width}/${image.height}) * (100vh - ${magicUnusable}vmin))`,
      };
      break;
  }

  // Use transformed components (following CMS methodology)
  const { mainComponents, sideComponents, allComponents } = transformedComponents;

  // Loading state (following CMS pattern)
  if (loading) {
    return <LoadingState containerRef={containerRef} />;
  }

  // Error state (following CMS pattern)
  if (error) {
    return <ErrorState containerRef={containerRef} error={error} screenId={screenId} orientation={orientation} />;
  }

  // No data state (following CMS pattern)
  if (!loading && allComponents.length === 0) {
    return <NoDataState containerRef={containerRef} signageDesign={signageDesign} screenId={screenId} />;
  }

  
  return (
    <div className='signage row panels panels-horizontal' style={{ margin: "0px" }}>
      <div ref={containerRef} style={{ 
        height: "100vh", 
        width: "100%", 
        display: "flex"
      }}>
        <MainArea
          mainComponents={mainComponents}
          gridConfig={gridConfig}
          renderGridComponent={renderGridComponent}
          imageUrl={imageUrl}
          markers={markers}
          planData={planData}
          legacySignage={legacySignage}
          categoriesData={categories}
          shortName={shortName}
          renderCountRef={renderCountRef}
        />
        
        <SideArea
          sideComponents={sideComponents}
          gridConfig={gridConfig}
          renderGridComponent={renderGridComponent}
          imageUrl={imageUrl}
          markers={markers}
          planData={planData}
          legacySignage={legacySignage}
          categoriesData={categories}
          shortName={shortName}
          renderCountRef={renderCountRef}
        />
      </div>
    </div>
  );
};
 
export default inject('signageState')(observer(Signage));